import React, { useState } from "react"
import {
  Col,
  Drawer,
  Row,
  Typography,
  Image,
  Space,
  Divider,
  Descriptions,
  Form,
  Input,
  Button,
  Upload,
  message,
} from "antd"
import { UploadOutlined } from "@ant-design/icons"
import { doc, updateDoc, collection, query, where, getDocs } from "firebase/firestore"
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from "firebase/storage"
import { db, storage } from "../../firebase/config"

const { Link, Text, Title, Paragraph } = Typography

export default function EditClientDrawer({ data }) {
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [imageUrl, setImageUrl] = useState(data.clientImage || "")
  const [form] = Form.useForm()

  const showDrawer = () => {
    form.setFieldsValue({
      client: data.client,
      clientGroup: data.clientGroup,
      vat: data.vat,
      reg: data.reg,
      street: data.street,
      postalCode: data.postalCode,
      city: data.city,
      country: data.country,
      deliveryAddress: data.deliveryAddress,
      deliveryAddress2: data.deliveryAddress2,
      contactName: data.contactName,
      contactEmail: data.contactEmail,
      contactNumber: data.contactNumber,
      bankName: data.bankName,
      bankCode: data.bankCode,
      accountNumber: data.accountNumber,
      accountant: data.accountant,
      accountantEmail: data.accountantEmail,
      accountantNumber: data.accountantNumber,
    })
    setImageUrl(data.clientImage || "")
    setOpen(true)
  }

  const onClose = () => {
    setEditing(false)
    setOpen(false)
  }

  const handleUpload = (info) => {
    const file = info.file.originFileObj
    if (!file) return
    const storageReference = storageRef(storage, `clientImages/${data.id}_${file.name}`)
    const uploadTask = uploadBytesResumable(storageReference, file)
    setUploading(true)
    uploadTask.on(
      "state_changed",
      null,
      (error) => {
        console.error(error.message)
        message.error("Image upload failed")
        setUploading(false)
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then((url) => {
          setImageUrl(url)
          form.setFieldsValue({ clientImage: url })
          message.success("Image uploaded")
          setUploading(false)
        })
      }
    )
  }

  const onFinish = async (values) => {
    setSubmitting(true)
    const updatedClient = { ...values, clientImage: imageUrl }
    try {
      // 1) Update client document
      const clientRef = doc(db, "clients", data.id)
      await updateDoc(clientRef, updatedClient)

      // 2) Propagate to priceCalculation collection
      const pcSnap = await getDocs(
        query(collection(db, "priceCalculation"), where("clientId", "==", data.id))
      )
      await Promise.all(
        pcSnap.docs.map((d) =>
          updateDoc(doc(db, "priceCalculation", d.id), {
            client: values.client,
          })
        )
      )

      // 3) Propagate to orders collection
      const ordersSnap = await getDocs(
        query(collection(db, "orders"), where("clientId", "==", data.id))
      )
      await Promise.all(
        ordersSnap.docs.map((d) =>
          updateDoc(doc(db, "orders", d.id), {
            // update nested client object
            "client.client": values.client,
            "client.vat": values.vat,
            "client.reg": values.reg,
            "client.street": values.street,
            "client.postalCode": values.postalCode,
            "client.city": values.city,
            "client.country": values.country,
            "client.deliveryAddress": values.deliveryAddress,
            "client.deliveryAddress2": values.deliveryAddress2,
            "client.contactName": values.contactName,
            "client.contactEmail": values.contactEmail,
            "client.contactNumber": values.contactNumber,
            "client.bankName": values.bankName,
            "client.bankCode": values.bankCode,
            "client.accountNumber": values.accountNumber,
            "client.accountant": values.accountant,
            "client.accountantEmail": values.accountantEmail,
            "client.accountantNumber": values.accountantNumber,
            "client.clientImage": imageUrl,
          })
        )
      )

      message.success("Client updated successfully")
      setEditing(false)
    } catch (error) {
      console.error(error.message)
      message.error("Failed to update client and related records")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <Link type="primary" onClick={showDrawer}>
        Edit
      </Link>
      <Drawer
        title={`Client: ${data.client}`}
        width={600}
        placement="left"
        onClose={onClose}
        open={open}
        destroyOnClose
        extra={
          <Space>
            {editing ? (
              <>
                <Button onClick={() => setEditing(false)}>Cancel</Button>
                <Button type="primary" onClick={() => form.submit()} loading={submitting}>
                  Save
                </Button>
              </>
            ) : (
              <Button onClick={() => setEditing(true)}>Edit</Button>
            )}
          </Space>
        }>
        {editing ? (
          <Form form={form} layout="vertical" style={{ width: "100%" }} onFinish={onFinish}>
            <Row gutter={[16, 16]}>
              <Col span={24} style={{ textAlign: "center" }}>
                <Image
                  width={150}
                  src={imageUrl}
                  fallback="https://firebasestorage.googleapis.com/v0/b/managers-testing.appspot.com/o/clients%2Fplaceholder_client.jpg?alt=media&token=d59cc6aa-03c6-459a-b974-e9c2610f0836"
                />
                <Upload showUploadList={false} customRequest={handleUpload}>
                  <Button icon={<UploadOutlined />} loading={uploading}>
                    Change Image
                  </Button>
                </Upload>
              </Col>
              {[
                ["Client Name", "client"],
                ["VAT", "vat"],
                ["Reg No", "reg"],
                ["Street", "street"],
                ["Postal Code", "postalCode"],
                ["City", "city"],
                ["Country", "country"],
                ["Delivery 1", "deliveryAddress"],
                ["Delivery 2", "deliveryAddress2"],
                ["Contact Name", "contactName"],
                ["Contact Email", "contactEmail"],
                ["Contact Number", "contactNumber"],
                ["Bank Name", "bankName"],
                ["Bank Code", "bankCode"],
                ["Account No", "accountNumber"],
                ["Accountant", "accountant"],
                ["Acct Email", "accountantEmail"],
                ["Acct Phone", "accountantNumber"],
              ].map(([label, name]) => (
                <Col span={12} key={name}>
                  <Form.Item
                    label={label}
                    name={name}
                    rules={label === "Client Name" || label === "VAT" ? [{ required: true }] : []}>
                    <Input size="small" />
                  </Form.Item>
                </Col>
              ))}
            </Row>
          </Form>
        ) : (
          <Row gutter={[16, 16]}>
            <Col flex="150px">
              <Image
                width={150}
                src={imageUrl}
                fallback="https://firebasestorage.googleapis.com/v0/b/managers-testing.appspot.com/o/clients%2Fplaceholder_client.jpg?alt=media&token=d59cc6aa-03c6-459a-b974-e9c2610f0836"
              />
            </Col>
            <Col flex="auto">
              <Space direction="vertical" size={2}>
                <Title level={4}>{data.client}</Title>
                {data.clientGroup && <Text>{data.clientGroup}</Text>}
                <Text>
                  {data.street}, {data.postalCode}
                </Text>
                <Text>
                  {data.city}, {data.country}
                </Text>
                <Paragraph copyable type="secondary">
                  ID: {data.id}
                </Paragraph>
              </Space>
            </Col>
            <Divider orientation="left">Financial & Bank</Divider>
            <Col span={24}>
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="VAT">{data.vat}</Descriptions.Item>
                <Descriptions.Item label="Reg No">{data.reg}</Descriptions.Item>
                <Descriptions.Item label="Credit Left">
                  {data.creditLeft}/{data.creditLimit}
                </Descriptions.Item>
                <Descriptions.Item label="Bank Name">{data.bankName}</Descriptions.Item>
                <Descriptions.Item label="Bank Code">{data.bankCode}</Descriptions.Item>
                <Descriptions.Item label="Account No">{data.accountNumber}</Descriptions.Item>
              </Descriptions>
            </Col>
            <Divider orientation="left">Delivery Addresses</Divider>
            <Col span={24}>
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Primary">{data.deliveryAddress}</Descriptions.Item>
                <Descriptions.Item label="Secondary">{data.deliveryAddress2}</Descriptions.Item>
              </Descriptions>
            </Col>
            <Divider orientation="left">Contacts</Divider>
            <Col span={24}>
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Contact Name">{data.contactName}</Descriptions.Item>
                <Descriptions.Item label="Contact Email">{data.contactEmail}</Descriptions.Item>
                <Descriptions.Item label="Contact Number">{data.contactNumber}</Descriptions.Item>
                <Descriptions.Item label="Accountant">{data.accountant}</Descriptions.Item>
                <Descriptions.Item label="Acct Email">{data.accountantEmail}</Descriptions.Item>
                <Descriptions.Item label="Acct Phone">{data.accountantNumber}</Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>
        )}
      </Drawer>
    </div>
  )
}
